import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import FaceProcessor from '../components/FaceProcessor.jsx';
import FaceGuideOverlay from '../components/FaceGuideOverlay';
import PageFooter from '../components/PageFooter';
import SalonHeaderActions from '../components/SalonHeaderActions';
import { calculateFaceGuidance, calculateSideFaceGuidance, calculateGuideBounds } from '../utils/faceGuidanceLogic';
import { useVoiceGuide } from '../hooks/useVoiceGuide';
import { initializeSession, getSessionIdFromUrl, getSessionId, pathWithSessionId, SESSION_STORAGE_KEY } from '../utils/sessionManager';
import { useSkinSession } from '../context/SkinSessionContext';
import { useSkinAnalysisPlan } from '../context/SkinAnalysisPlanContext';
import { fetchAQIData } from '../utils/aqiFetcher';
import { getApiBaseUrl, SALON_API_PREFIX } from '../utils/apiConfig';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import CameraPrepPlaceholder from '../../enterprise/shared/CameraPrepPlaceholder';
import '../../enterprise/shared/camera-capture-shell.css';
import '../styles/PageBackground.css';
import '../styles/ActionButtons.css';
import './FaceDetectionPage.css';

const FaceDetectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, sessionLandingPath } = useSkinSession();
  const { ENABLE_SIDE_CAPTURE, isReady: isCaptureModeReady } = useSkinAnalysisPlan();

  // Get user info from UserInfoPage (gender, skinType, age)
  const userInfoFromState = location.state || {};

  // Refs
  const videoRef = useRef(null);
  const [videoEl, setVideoEl] = useState(null);
  const setVideoNode = useCallback((node) => {
    videoRef.current = node;
    setVideoEl(node);
  }, []);
  const canvasRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const frameTimerRef = useRef(null);
  const frameLoopActiveRef = useRef(false);
  const frameInFlightRef = useRef(false);
  const faceDetectedRef = useRef(false);
  const lightingStatusRef = useRef('checking');
  const perfectAlignmentTimerRef = useRef(null);
  const perfectAlignmentStartTimeRef = useRef(null);
  const gracePeriodTimerRef = useRef(null);
  const hasAutoCapturedRef = useRef(false);
  const lastLandmarksRef = useRef(null);
  const lastVideoDimensionsRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const handleCaptureRef = useRef(null);
  const isCameraInitializingRef = useRef(false);
  const lastBackendLightingCheckRef = useRef(0);
  const lightingMessageRef = useRef('');
  const captureStepRef = useRef('front');

  // Match Enterprise face-identity: 720p selfie stream (MediaPipe is reliable; 4K often fails silently)
  const getFrameConfig = () => ({
    width: 1280,
    minDelay: 220,
    quality: 0.9,
    videoConstraints: {
      facingMode: 'user',
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 30, max: 30 },
    },
  });

  const frameConfigRef = useRef(getFrameConfig());

  const autoCaptureDelay = 3000; // 3 seconds of perfect alignment (green status only)
  const gracePeriod = 500; // 500ms grace period before resetting timer

  // State
  const [faceGuidance, setFaceGuidance] = useState({
    isInsideFrame: false,
    statusColor: 'red',
    message: 'Position your face',
  });
  const [lightingStatus, setLightingStatus] = useState('checking'); // 'good', 'moderate', 'poor'
  const [lightingMessage, setLightingMessage] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [previewEnabled, setPreviewEnabled] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [countdown, setCountdown] = useState(null);
  // Session: state > URL (?session_id=) > localStorage so flow works when state is lost
  const [sessionId, setSessionId] = useState(() => location.state?.sessionId ?? getSessionIdFromUrl(location) ?? getSessionId() ?? null);
  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);
  const [saveError, setSaveError] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showVoicePermissionModal, setShowVoicePermissionModal] = useState(true);
  const [voiceGuideEnabled, setVoiceGuideEnabled] = useState(null);
  const [isProceeding, setIsProceeding] = useState(false);
  // Unified capture: 'front' | 'left' | 'right' | 'done'. Side only for growth/pro.
  const [captureStep, setCaptureStep] = useState('front');
  const [sideCaptures, setSideCaptures] = useState({ left: null, right: null });
  const [sideCapturesSkipped, setSideCapturesSkipped] = useState(false);
  const [previewImageTab, setPreviewImageTab] = useState('front');
  const lightingPass = lightingStatus === 'good' || lightingStatus === 'moderate';

  const guidedStepForVoice = captureStep === 'done' ? 'front' : captureStep;
  const isAlignedForVoice = faceGuidance.statusColor === 'green';
  const { unlockForIOS } = useVoiceGuide(
    guidedStepForVoice,
    isAlignedForVoice,
    isCapturing,
    voiceGuideEnabled === true && previewEnabled && captureStep !== 'done',
    lightingPass,
    lightingMessage || '',
    faceGuidance.message || '',
    lightingStatus !== 'checking'
  );
  const speechUnlockedRef = useRef(false);
  const [showVoiceHint, setShowVoiceHint] = useState(false);
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
  useEffect(() => {
    if (voiceGuideEnabled && isIOS && previewEnabled && captureStep !== 'done' && !speechUnlockedRef.current) {
      setShowVoiceHint(true);
    } else {
      setShowVoiceHint(false);
    }
  }, [voiceGuideEnabled, isIOS, previewEnabled, captureStep]);
  const handleUnlockSpeech = useCallback(() => {
    if (captureStep === 'done') return;
    if (voiceGuideEnabled && !speechUnlockedRef.current && unlockForIOS) {
      speechUnlockedRef.current = true;
      setShowVoiceHint(false);
      unlockForIOS();
    }
  }, [captureStep, voiceGuideEnabled, unlockForIOS]);

  // Check lighting from video frame. Backend-only helper (non-blocking for capture).
  const LIGHTING_CHECK_THROTTLE_MS = 1000;

  const checkLighting = useCallback((canvasEl) => {
    if (!canvasEl) return;

    const baseUrl = getApiBaseUrl();
    if (!baseUrl) {
      // Fail open for transient connectivity/config issues so capture flow can continue.
      setLightingStatus('good');
      setLightingMessage('');
      lightingStatusRef.current = 'good';
      lightingMessageRef.current = '';
      return;
    }

    if (Date.now() - lastBackendLightingCheckRef.current <= LIGHTING_CHECK_THROTTLE_MS) {
      return;
    }

    lastBackendLightingCheckRef.current = Date.now();
    const frameDataUrl = canvasEl.toDataURL('image/jpeg', 0.8);

    fetchWithTimeout(`${baseUrl.replace(/\/$/, '')}${SALON_API_PREFIX}/check-lighting`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        image: frameDataUrl,
        session_id: sessionIdRef.current || undefined,
        profile_mode: captureStepRef.current === 'left' || captureStepRef.current === 'right',
      }),
      timeoutMs: 8000,
    })
      .then((res) => res.json())
      .then((d) => {
        // Map backend category (Good / Moderate / Bad) to frontend status
        const statusMap = { good: 'good', moderate: 'moderate', bad: 'poor' };
        const normalizedStatus = String(d?.status || '').toLowerCase();
        const backendStatus = (d?.lighting_ok === true || normalizedStatus === 'good' || normalizedStatus === 'moderate')
          ? 'good'
          : (statusMap[normalizedStatus] || 'poor');
        setLightingStatus(backendStatus);
        setLightingMessage(d.message || '');
        lightingStatusRef.current = backendStatus;
        lightingMessageRef.current = d.message || '';
      })
      .catch(() => {
        // Network timeouts/errors should not block capture.
        setLightingStatus('good');
        setLightingMessage('');
        lightingStatusRef.current = 'good';
        lightingMessageRef.current = '';
      });
  }, []);

  // Start camera
  const startCamera = useCallback(async () => {
    if (typeof navigator === 'undefined') {
      return;
    }
    if (mediaStreamRef.current) {
      // If stream exists but video is paused (e.g. after capture), resume it
      if (videoRef.current && videoRef.current.paused) {
        try {
          await videoRef.current.play();
        } catch (e) {
          console.error("Error resuming video:", e);
        }
        setPreviewEnabled(true);
      }
      return;
    }

    if (isCameraInitializingRef.current) {
      return;
    }

    isCameraInitializingRef.current = true;

    try {
      const config = frameConfigRef.current;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: config.videoConstraints,
        audio: false,
      });

      // Check if we should still be running (e.g. unmounted/stopped while waiting)
      if (!isCameraInitializingRef.current) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      mediaStreamRef.current = stream;
      const videoEl = videoRef.current;

      if (videoEl) {
        try {
          if (!videoEl.paused) {
            await videoEl.pause();
          }
        } catch (_) {
          /* ignore */
        }
        videoEl.setAttribute('playsinline', 'true');
        videoEl.setAttribute('muted', 'true');
        videoEl.muted = true;
        videoEl.srcObject = stream;
        const playPromise = videoEl.play();
        if (playPromise && typeof playPromise.then === 'function') {
          await playPromise.catch((error) => {
            if (error && error.name !== 'AbortError') {
              throw error;
            }
          });
        }
        setPreviewEnabled(true);
        // Fail-open until first backend check returns (avoids stuck on "checking").
        setLightingStatus('good');
        setLightingMessage('');
        lightingStatusRef.current = 'good';
        lightingMessageRef.current = '';
      }
    } catch (error) {
      if (isCameraInitializingRef.current) {
        setPreviewEnabled(false);
        let errorMessage = 'Unable to access camera. ';
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          errorMessage += 'Please grant camera permissions.';
        } else if (error.name === 'NotFoundError') {
          errorMessage += 'No camera found.';
        } else {
          errorMessage += 'Please check your camera settings.';
        }
        alert(errorMessage);
      }
    } finally {
      isCameraInitializingRef.current = false;
    }
  }, []);

  // Stop camera
  const stopCamera = useCallback(() => {
    isCameraInitializingRef.current = false;
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      mediaStreamRef.current = null;
    }
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      } catch (err) {
      }
    }
    setPreviewEnabled(false);
  }, []);

  // Stop frame loop
  const stopFrameLoop = useCallback(() => {
    frameLoopActiveRef.current = false;
    if (frameTimerRef.current) {
      clearTimeout(frameTimerRef.current);
      frameTimerRef.current = null;
    }
    frameInFlightRef.current = false;
  }, []);

  // Send frame for processing
  const sendFrame = useCallback(async () => {
    if (
      !videoRef.current ||
      !canvasRef.current ||
      !mediaStreamRef.current ||
      frameInFlightRef.current
    ) {
      return false;
    }
    const videoEl = videoRef.current;
    if (videoEl.readyState < 2) {
      return false;
    }

    const canvasEl = canvasRef.current;
    const { width: targetWidth } = frameConfigRef.current || {};
    const WIDTH = targetWidth || 3840;
    const HEIGHT =
      Math.round((videoEl.videoHeight / videoEl.videoWidth) * WIDTH) ||
      Math.round((9 / 16) * WIDTH) ||
      2160;

    canvasEl.width = WIDTH;
    canvasEl.height = HEIGHT;
    const ctx = canvasEl.getContext('2d', { willReadFrequently: true, alpha: false });
    if (!ctx) {
      return false;
    }
    ctx.drawImage(videoEl, 0, 0, WIDTH, HEIGHT);

    // Check lighting from the frame
    checkLighting(canvasEl);

    return true;
  }, [checkLighting]);

  // Frame loop
  const runFrameLoop = useCallback(async () => {
    if (!frameLoopActiveRef.current) {
      return;
    }
    const getNow =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? () => performance.now()
        : () => Date.now();
    const startedAt = getNow();
    const sent = await sendFrame();
    const finishedAt = getNow();
    const elapsed = finishedAt - startedAt;

    const config = frameConfigRef.current || {};
    const baseDelay = config.minDelay || 220;
    const fallbackDelay = baseDelay + 120;
    const dynamicDelay = sent ? Math.max(baseDelay, elapsed * 1.25) : fallbackDelay;
    const nextDelay = Math.min(Math.max(dynamicDelay, baseDelay), baseDelay + 800);

    if (!frameLoopActiveRef.current) {
      return;
    }
    frameTimerRef.current = setTimeout(runFrameLoop, nextDelay);
  }, [sendFrame]);

  // Start frame loop
  const startFrameLoop = useCallback(async () => {
    await startCamera();
    if (frameLoopActiveRef.current) {
      return;
    }
    frameLoopActiveRef.current = true;
    frameTimerRef.current = setTimeout(runFrameLoop, 0);
  }, [startCamera, runFrameLoop]);

  // Handle face landmarks from MediaPipe
  const handleLandmarksDetected = useCallback((landmarks, videoDimensions) => {
    const faceDetected = Boolean(landmarks && landmarks.length > 0);
    faceDetectedRef.current = faceDetected;

    // Store landmarks for cropping
    if (landmarks && landmarks.length > 0) {
      lastLandmarksRef.current = landmarks;
      lastVideoDimensionsRef.current = videoDimensions;
    }

    if (landmarks && videoDimensions) {
      const ovalBounds = calculateGuideBounds(videoDimensions.width, videoDimensions.height);
      const lightingOk = lightingStatusRef.current === 'good' || lightingStatusRef.current === 'moderate';
      const lightingMsg = lightingMessageRef.current || '';

      let guidance;
      if (captureStep === 'left' || captureStep === 'right') {
        guidance = calculateSideFaceGuidance(
          landmarks,
          videoDimensions.width,
          videoDimensions.height,
          captureStep,
          lightingOk,
          lightingMsg
        );
      } else {
        guidance = calculateFaceGuidance(
          landmarks,
          videoDimensions.width,
          videoDimensions.height,
          ovalBounds,
          lightingOk,
          lightingMsg
        );
      }

      setFaceGuidance(guidance);

      const isLiveVideoActive = Boolean(previewEnabled && captureStep !== 'done');
      const qualifiesForCapture = (
        guidance.isInsideFrame &&
        guidance.statusColor === 'green' &&
        guidance.isFaceNear &&
        guidance.isFaceStraight &&
        faceDetected &&
        lightingOk
      ) &&
        !isCapturing &&
        !hasAutoCapturedRef.current &&
        isLiveVideoActive;

      // Auto-capture delay - only used for green status
      const captureDelay = autoCaptureDelay;

      if (qualifiesForCapture) {
        // Clear any grace period timer since we're in good alignment
        if (gracePeriodTimerRef.current) {
          clearTimeout(gracePeriodTimerRef.current);
          gracePeriodTimerRef.current = null;
        }

        // Start or continue alignment timer with countdown
        if (!perfectAlignmentStartTimeRef.current) {
          // Start new timer
          perfectAlignmentStartTimeRef.current = Date.now();
          const initialCountdown = Math.ceil(captureDelay / 1000);
          setCountdown(initialCountdown);

          if (perfectAlignmentTimerRef.current) {
            clearTimeout(perfectAlignmentTimerRef.current);
          }

          // Clear any existing countdown interval
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }

          // Start countdown interval
          countdownIntervalRef.current = setInterval(() => {
            if (perfectAlignmentStartTimeRef.current) {
              const elapsed = Date.now() - perfectAlignmentStartTimeRef.current;
              const remaining = Math.ceil((captureDelay - elapsed) / 1000);

              if (remaining > 0) {
                setCountdown(remaining);
              } else {
                if (countdownIntervalRef.current) {
                  clearInterval(countdownIntervalRef.current);
                  countdownIntervalRef.current = null;
                }
                setCountdown(0);
              }
            } else {
              if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
              }
            }
          }, 50);

          perfectAlignmentTimerRef.current = setTimeout(() => {
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }
            hasAutoCapturedRef.current = true;
            perfectAlignmentStartTimeRef.current = null;
            perfectAlignmentTimerRef.current = null;
            setCountdown(null);
            // Trigger capture
            if (handleCaptureRef.current) {
              handleCaptureRef.current();
            }
          }, captureDelay);
        }
      } else {
        // Not in good alignment OR face not straight - reset countdown immediately
        if (!guidance.isFaceStraight) {
          // Face is not straight - immediately reset everything
          setCountdown(null);
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          if (perfectAlignmentTimerRef.current) {
            clearTimeout(perfectAlignmentTimerRef.current);
            perfectAlignmentTimerRef.current = null;
          }
          perfectAlignmentStartTimeRef.current = null;
        } else {
          // Other alignment issues - reset countdown and start grace period
          setCountdown(null);
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          if (perfectAlignmentStartTimeRef.current) {
            if (!gracePeriodTimerRef.current) {
              gracePeriodTimerRef.current = setTimeout(() => {
                perfectAlignmentStartTimeRef.current = null;
                gracePeriodTimerRef.current = null;
                if (perfectAlignmentTimerRef.current) {
                  clearTimeout(perfectAlignmentTimerRef.current);
                  perfectAlignmentTimerRef.current = null;
                }
              }, gracePeriod);
            }
          }
        }
      }
    } else {
      // No landmarks detected
      setFaceGuidance({
        isInsideFrame: false,
        statusColor: 'red',
        message: 'Position your face',
      });

      // Reset auto-capture timer if no face detected
      if (perfectAlignmentStartTimeRef.current) {
        perfectAlignmentStartTimeRef.current = null;
        if (perfectAlignmentTimerRef.current) {
          clearTimeout(perfectAlignmentTimerRef.current);
          perfectAlignmentTimerRef.current = null;
        }
        if (gracePeriodTimerRef.current) {
          clearTimeout(gracePeriodTimerRef.current);
          gracePeriodTimerRef.current = null;
        }
      }
    }
  }, [previewEnabled, captureStep, isCapturing]);

  // Calculate face bounding box from landmarks.
  // Always returns a SQUARE crop (same shape as frontal) so front/left/right have consistent output.
  const calculateFaceBoundingBox = useCallback((landmarks, videoWidth, videoHeight, padding = 0.25) => {
    if (!landmarks || !landmarks.length) {
      return null;
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    landmarks.forEach((landmark) => {
      const x = typeof landmark.x === 'number' ? landmark.x : (Array.isArray(landmark) ? landmark[0] : null);
      const y = typeof landmark.y === 'number' ? landmark.y : (Array.isArray(landmark) ? landmark[1] : null);

      if (x !== null && y !== null && typeof x === 'number' && typeof y === 'number') {
        const px = x * videoWidth;
        const py = y * videoHeight;

        minX = Math.min(minX, px);
        minY = Math.min(minY, py);
        maxX = Math.max(maxX, px);
        maxY = Math.max(maxY, py);
      }
    });

    if (minX === Infinity || minY === Infinity) {
      return null;
    }

    const width = maxX - minX;
    const height = maxY - minY;
    const horizontalPadding = width * padding;
    const topPadding = height * 0.42;
    const bottomPadding = height * 0.2;
    const left = minX - horizontalPadding;
    const right = maxX + horizontalPadding;
    const top = minY - topPadding;
    const bottom = maxY + bottomPadding;
    const expandedWidth = right - left;
    const expandedHeight = bottom - top;
    const size = Math.min(Math.max(expandedWidth, expandedHeight), videoWidth, videoHeight);
    if (size <= 0) return null;

    const expandedCenterX = (left + right) / 2;
    const expandedCenterY = (top + bottom) / 2;
    let cropX = expandedCenterX - size / 2;
    let cropY = expandedCenterY - size / 2;
    cropX = Math.max(0, Math.min(cropX, videoWidth - size));
    cropY = Math.max(0, Math.min(cropY, videoHeight - size));

    return {
      x: Math.round(cropX),
      y: Math.round(cropY),
      width: Math.round(size),
      height: Math.round(size),
    };
  }, []);


  // Handle capture - supports front, left, right in same flow. Block if lighting not good.
  const handleCapture = useCallback(() => {
    if (!isCaptureModeReady || !videoRef.current || !canvasRef.current || isCapturing) {
      return;
    }
    if (lightingStatusRef.current !== 'good') {
      return;
    }

    const step = captureStepRef.current;

    setIsCapturing(true);
    const videoEl = videoRef.current;
    const canvasEl = canvasRef.current;

    const { width: targetWidth, quality } = frameConfigRef.current || {};
    const WIDTH = Math.min(targetWidth || 1920, 1920);
    const HEIGHT =
      Math.round((videoEl.videoHeight / videoEl.videoWidth) * WIDTH) ||
      Math.round((9 / 16) * WIDTH) ||
      1080;

    canvasEl.width = WIDTH;
    canvasEl.height = HEIGHT;
    const ctx = canvasEl.getContext('2d', {
      willReadFrequently: true,
      alpha: false,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high'
    });
    if (!ctx) {
      setIsCapturing(false);
      return;
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(videoEl, 0, 0, WIDTH, HEIGHT);

    let croppedDataUrl = null;
    if (lastLandmarksRef.current && lastVideoDimensionsRef.current) {
      const boundingBox = calculateFaceBoundingBox(
        lastLandmarksRef.current,
        WIDTH,
        HEIGHT,
        0.25
      );

      if (boundingBox) {
        const cropCanvas = document.createElement('canvas');
        cropCanvas.width = Math.round(boundingBox.width);
        cropCanvas.height = Math.round(boundingBox.height);
        const cropCtx = cropCanvas.getContext('2d', {
          willReadFrequently: true,
          alpha: false,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high'
        });

        if (cropCtx) {
          cropCtx.imageSmoothingEnabled = true;
          cropCtx.imageSmoothingQuality = 'high';
          cropCtx.drawImage(
            canvasEl,
            Math.round(boundingBox.x),
            Math.round(boundingBox.y),
            Math.round(boundingBox.width),
            Math.round(boundingBox.height),
            0,
            0,
            Math.round(boundingBox.width),
            Math.round(boundingBox.height)
          );
          croppedDataUrl = cropCanvas.toDataURL('image/jpeg', 0.95);
        }
      }
    }

    const finalImage = croppedDataUrl || canvasEl.toDataURL('image/jpeg', quality ?? 0.95);

    if (step === 'front') {
      setCapturedImage(finalImage);
      if (ENABLE_SIDE_CAPTURE) {
        captureStepRef.current = 'left';
        setCaptureStep('left');
        hasAutoCapturedRef.current = false;
        if (videoEl.paused) {
          videoEl.play().catch(() => {});
        }
      } else {
        setPreviewEnabled(false);
        if (videoEl) {
          try {
            videoEl.pause();
            videoEl.srcObject = null;
          } catch (err) {
          }
        }
        captureStepRef.current = 'done';
        setCaptureStep('done');
        stopFrameLoop();
        stopCamera();
      }
    } else if (step === 'left') {
      setSideCaptures((prev) => ({ ...prev, left: finalImage }));
      captureStepRef.current = 'right';
      setCaptureStep('right');
      hasAutoCapturedRef.current = false;
      if (videoEl.paused) {
        videoEl.play().catch(() => {});
      }
    } else if (step === 'right') {
      setSideCaptures((prev) => ({ ...prev, right: finalImage }));
      setPreviewEnabled(false);
      if (videoEl) {
        try {
          videoEl.pause();
          videoEl.srcObject = null;
        } catch (err) {
        }
      }
      captureStepRef.current = 'done';
      setCaptureStep('done');
      stopFrameLoop();
      stopCamera();
    }

    setIsCapturing(false);
  }, [isCaptureModeReady, isCapturing, stopFrameLoop, calculateFaceBoundingBox, stopCamera, ENABLE_SIDE_CAPTURE]);

  useEffect(() => {
    handleCaptureRef.current = handleCapture;
  }, [handleCapture]);

  useEffect(() => {
    captureStepRef.current = captureStep;
  }, [captureStep]);

  // Sync sessionId from URL when landing with ?session_id= or from storage
  useEffect(() => {
    const fromUrl = getSessionIdFromUrl(location);
    const fromStorage = getSessionId();
    if (fromUrl && fromUrl !== sessionId) {
      setSessionId(fromUrl);
      try {
        localStorage.setItem(SESSION_STORAGE_KEY, fromUrl);
      } catch (e) { }
    } else if (fromStorage && !sessionId) {
      setSessionId(fromStorage);
    }
  }, [location, sessionId]);
  // Initialize session on mount only when we don't already have one (e.g. from UserInfoPage or URL)
  useEffect(() => {
    if (sessionId) return;
    const initSession = async () => {
      const API_BASE_URL = getApiBaseUrl() || '';
      const sid = await initializeSession(API_BASE_URL, token ? { app_session_token: token } : {});
      setSessionId(sid);
    };
    initSession();
  }, [sessionId, token]);

  /** Proceed to skin analysis: contact was saved on User Info — no duplicate save here. */
  const handleProceedToAnalysis = async () => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setSaveError(null);
    setIsProceeding(true);
    try {
      const existingId = sessionId ?? location.state?.sessionId;
      let finalSessionId = existingId;
      if (!finalSessionId) {
        const API_BASE_URL = getApiBaseUrl() || '';
        finalSessionId = await initializeSession(API_BASE_URL, token ? { app_session_token: token } : {});
        setSessionId(finalSessionId);
      }

      const aqiData = await fetchAQIData(finalSessionId);
      const st = location.state || {};
      const userData = {
        name: st.name || '',
        mobileNumber: st.mobileNumber || st.phone || '',
        email: st.email || '',
        instagramId: st.instagramId || '',
      };

      navigate(pathWithSessionId('/skin-analysis', finalSessionId), {
        state: {
          capturedImage,
          sideCaptures: sideCapturesSkipped ? undefined : (sideCaptures?.left || sideCaptures?.right ? sideCaptures : undefined),
          userData: {
            ...userData,
            gender: st.gender || null,
            skinType: st.skinType || null,
            age: st.age ?? null,
          },
          sessionId: finalSessionId,
          aqiData,
          ...(token && { token }),
        },
      });
    } catch (error) {
      setSaveError(error.message || 'Failed to proceed');
    } finally {
      setIsProceeding(false);
    }
  };

  // Start camera/MediaPipe after voice modal — do NOT wait for plan ready
  // (token/plan fetch flipping isReady was stopping the camera and breaking FaceMesh).
  useEffect(() => {
    if (!showVoicePermissionModal) {
      startFrameLoop();
    }
    return () => {
      stopFrameLoop();
      stopCamera();
    };
  }, [showVoicePermissionModal, startFrameLoop, stopFrameLoop, stopCamera]);

  const handleBack = () => {
    const sid = sessionId ?? location.state?.sessionId ?? getSessionIdFromUrl(location) ?? getSessionId();
    const path = pathWithSessionId('/user-info', sid);
    navigate(path, {
      state: {
        userInfo: userInfoFromState,
        ...(sid ? { sessionId: sid } : {})
      }
    });
  };

  const handleHome = () => {
    navigate(sessionLandingPath);
  };

  const previewTabs = ['front'];
  if (sideCaptures.left) previewTabs.push('left');
  if (sideCaptures.right) previewTabs.push('right');
  const normalizedPreviewImageTab = previewTabs.includes(previewImageTab) ? previewImageTab : 'front';
  const previewTabIndex = previewTabs.indexOf(normalizedPreviewImageTab);
  const canCyclePreview = previewTabs.length > 1;
  const handlePreviewPrevious = () => {
    if (!canCyclePreview) return;
    const nextIndex = (previewTabIndex - 1 + previewTabs.length) % previewTabs.length;
    setPreviewImageTab(previewTabs[nextIndex]);
  };
  const handlePreviewNext = () => {
    if (!canCyclePreview) return;
    const nextIndex = (previewTabIndex + 1) % previewTabs.length;
    setPreviewImageTab(previewTabs[nextIndex]);
  };

  return (
    <div className="face-detection-page page-bg">
      {/* Navigation Buttons */}
      <div className="face-detection-page-nav" onClick={handleUnlockSpeech} role="presentation">
        <button
          type="button"
          className="face-detection-page-back-button"
          onClick={handleBack}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="face-detection-nav-right">
          <button
            type="button"
            className="face-detection-info-button"
            onClick={() => setShowInfoModal(true)}
            aria-label="Show tips for perfect results"
            title="Tips for perfect results"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </button>
          <SalonHeaderActions homeButtonClassName="face-detection-page-home-button" onHome={handleHome} />
        </div>
      </div>

      <main
        className={`face-detection-page-main${showVoicePermissionModal ? ' face-detection-page-main--awaiting-voice' : ''}`}
        onClick={handleUnlockSpeech}
        role="presentation"
      >
        {!showVoicePermissionModal ? (
          <>
            <video
              ref={setVideoNode}
              playsInline
              muted
              className={`video-preview ${!previewEnabled || captureStep === 'done' ? 'video-preview--preparing' : ''}${captureStep === 'done' ? ' video-preview--hidden' : ''}`}
            />
            {!previewEnabled && captureStep !== 'done' ? (
              <CameraPrepPlaceholder variant="dark" message="Starting camera…" />
            ) : null}
          </>
        ) : null}
        <canvas ref={canvasRef} className="capture-canvas" />

        {/* iOS: Enable voice button (Safari requires user gesture - tap this to unlock speech) */}
        {voiceGuideEnabled && isIOS && previewEnabled && captureStep !== 'done' && showVoiceHint && (
          <button
            type="button"
            className="face-detection-voice-enable-btn"
            onClick={handleUnlockSpeech}
            aria-label="Enable voice guide"
          >
            🔊 Turn on voice guide (ensure sound is on)
          </button>
        )}

        {previewEnabled && captureStep !== 'done' && (
          <FaceGuideOverlay
            statusColor={faceGuidance.statusColor}
            message={faceGuidance.message}
            videoWidth={videoRef.current?.videoWidth || 640}
            videoHeight={videoRef.current?.videoHeight || 480}
            showOverlay={true}
            countdown={countdown}
            lightingOk={lightingPass}
            faceDetected={faceDetectedRef.current}
            faceStraight={faceGuidance.isFaceStraight || false}
            voiceDisabled={voiceGuideEnabled === false}
            headTurnDirection={
              captureStep === 'left' ? 'right' : captureStep === 'right' ? 'left' : null
            }
          />
        )}

        {previewEnabled && captureStep !== 'done' && videoEl && (
          <FaceProcessor
            videoElement={videoEl}
            onLandmarksDetected={handleLandmarksDetected}
            enabled={previewEnabled && captureStep !== 'done'}
          />
        )}

        {/* Captured Image Preview - when all capture done */}
        {capturedImage && captureStep === 'done' && (
          <div className="captured-preview" onClick={(e) => e.stopPropagation()}>
            <div className="captured-preview__viewer">
              {canCyclePreview && (
                <button
                  type="button"
                  className="captured-preview__nav captured-preview__nav--left"
                  onClick={handlePreviewPrevious}
                  aria-label="Show previous capture"
                  disabled={isProceeding}
                >
                  {'<'}
                </button>
              )}

              <div className="captured-preview__media-frame">
                <img
                  src={
                    normalizedPreviewImageTab === 'front'
                      ? capturedImage
                      : normalizedPreviewImageTab === 'left'
                        ? sideCaptures.left
                        : sideCaptures.right
                  }
                  alt={`Captured ${normalizedPreviewImageTab}`}
                />
              </div>

              {canCyclePreview && (
                <button
                  type="button"
                  className="captured-preview__nav captured-preview__nav--right"
                  onClick={handlePreviewNext}
                  aria-label="Show next capture"
                  disabled={isProceeding}
                >
                  {'>'}
                </button>
              )}

            </div>

            <div className="capture-actions">
              <button
                className="skin-action-button-secondary skin-action-button"
                style={{ minWidth: '120px' }}
                disabled={isProceeding}
                onClick={() => {
                  setCapturedImage(null);
                  setSideCaptures({ left: null, right: null });
                  setSideCapturesSkipped(false);
                  setPreviewImageTab('front');
                  setCaptureStep('front');
                  captureStepRef.current = 'front';
                  hasAutoCapturedRef.current = false;
                  startFrameLoop();
                }}
              >
                Retake
              </button>
              <button
                type="button"
                className="skin-action-button"
                style={{ minWidth: '120px' }}
                onClick={handleProceedToAnalysis}
                disabled={isProceeding}
              >
                {isProceeding ? 'Proceeding…' : 'Proceed'}
              </button>
              {saveError && (
                <p className="face-detection-proceed-error" role="alert">
                  {saveError}
                </p>
              )}
            </div>

            {isProceeding && (
              <div className="face-detection-loading-overlay" role="status" aria-live="polite" aria-label="Preparing skin analysis">
                <div className="face-detection-loading-content">
                  <div className="loading-spinner" aria-hidden="true" />
                  <p className="loading-text">Preparing your skin analysis...</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Voice Guide Permission Modal - shown before capture starts */}
        {showVoicePermissionModal && (
          <div className="voice-permission-modal-overlay">
            <div className="voice-permission-modal" onClick={(e) => e.stopPropagation()}>
              <div className="voice-permission-modal-header">
                <h2 className="voice-permission-modal-title">Voice Guide</h2>
              </div>
              <div className="voice-permission-modal-body">
                <p className="voice-permission-modal-text">
                  Would you like to enable voice guidance during capture? It will guide you to position your face correctly.
                </p>
              </div>
              <div className="voice-permission-modal-footer">
                <button
                  type="button"
                  className="skin-action-button voice-permission-cta"
                  onClick={() => {
                    setVoiceGuideEnabled(true);
                    setShowVoicePermissionModal(false);
                  }}
                >
                  Enable
                </button>
                <button
                  type="button"
                  className="skin-action-button skin-action-button-secondary voice-permission-secondary"
                  onClick={() => {
                    setVoiceGuideEnabled(false);
                    setShowVoicePermissionModal(false);
                  }}
                >
                  Disable
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Info Modal for Perfect Results Tips */}
        {showInfoModal && (
          <div className="info-modal-overlay" onClick={() => setShowInfoModal(false)}>
            <div className="info-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="info-modal-header">
                <h2 className="info-modal-title">Tips for Perfect Results</h2>
                <button
                  className="info-modal-close-button"
                  onClick={() => setShowInfoModal(false)}
                  aria-label="Close tips"
                >
                  ×
                </button>
              </div>

              <div className="info-modal-body">
                <div className="info-tips-list">
                  <div className="info-tip-item">
                    <div className="info-tip-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                    </div>
                    <div className="info-tip-content">
                      <h3 className="info-tip-title">Position Your Face</h3>
                      <p className="info-tip-text">Center your face within the guide frame. Keep your face straight and look directly at the camera.</p>
                    </div>
                  </div>

                  <div className="info-tip-item">
                    <div className="info-tip-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="5"></circle>
                        <line x1="12" y1="1" x2="12" y2="3"></line>
                        <line x1="12" y1="21" x2="12" y2="23"></line>
                        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                        <line x1="1" y1="12" x2="3" y2="12"></line>
                        <line x1="21" y1="12" x2="23" y2="12"></line>
                        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                      </svg>
                    </div>
                    <div className="info-tip-content">
                      <h3 className="info-tip-title">Good Lighting</h3>
                      <p className="info-tip-text">Ensure you have front-facing, even lighting across your face. Avoid shadows and backlighting.</p>
                    </div>
                  </div>

                  <div className="info-tip-item">
                    <div className="info-tip-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                    </div>
                    <div className="info-tip-content">
                      <h3 className="info-tip-title">Remove Accessories</h3>
                      <p className="info-tip-text">Remove glasses, hats, or any accessories that might obstruct your face. Pull your hair back if needed.</p>
                    </div>
                  </div>

                  <div className="info-tip-item">
                    <div className="info-tip-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="3" y1="12" x2="21" y2="12"></line>
                        <line x1="3" y1="6" x2="21" y2="6"></line>
                        <line x1="3" y1="18" x2="21" y2="18"></line>
                      </svg>
                    </div>
                    <div className="info-tip-content">
                      <h3 className="info-tip-title">Maintain Distance</h3>
                      <p className="info-tip-text">Keep an appropriate distance from the camera - not too close, not too far. The guide will help you position correctly.</p>
                    </div>
                  </div>

                  <div className="info-tip-item">
                    <div className="info-tip-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                    <div className="info-tip-content">
                      <h3 className="info-tip-title">Stay Still</h3>
                      <p className="info-tip-text">Once you see the green guide and countdown starts, hold still until the photo is captured automatically.</p>
                    </div>
                  </div>

                  <div className="info-tip-item">
                    <div className="info-tip-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </div>
                    <div className="info-tip-content">
                      <h3 className="info-tip-title">Natural Expression</h3>
                      <p className="info-tip-text">Keep a natural, relaxed expression. Avoid smiling too much or making exaggerated facial expressions.</p>
                    </div>
                  </div>

                  <div className="info-tip-item">
                    <div className="info-tip-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="5"></circle>
                        <line x1="12" y1="1" x2="12" y2="3"></line>
                        <line x1="12" y1="21" x2="12" y2="23"></line>
                        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                        <line x1="1" y1="12" x2="3" y2="12"></line>
                        <line x1="21" y1="12" x2="23" y2="12"></line>
                        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                        <path d="M8 12h.01M12 12h.01M16 12h.01"></path>
                      </svg>
                    </div>
                    <div className="info-tip-content">
                      <h3 className="info-tip-title">Lighting & Makeup</h3>
                      <p className="info-tip-text">Results may vary depending on lighting conditions and if makeup is applied. For best accuracy, use natural lighting and avoid heavy makeup.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-modal-footer">
                <button
                  className="skin-action-button"
                  style={{ maxWidth: '200px', margin: '0 auto' }}
                  onClick={() => setShowInfoModal(false)}
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      <PageFooter />
    </div>
  );
};

export default FaceDetectionPage;

